import { Navigate, Outlet, useLocation } from 'react-router-dom';
import {
  nhAppMainPages,
  isRouteAccessible,
  isRouteSupportedAtScope,
} from '@pflegenavi/frontend/routing';
import {
  useCanLeaveNursingHomeScope,
  useEmployeeIsNursingHomeAdminOrManager,
} from './roleBasedRestrictions';
import { useIsNursingHomeScope, useScope } from './ScopeContext';

export function RouteAccessGuard() {
  const location = useLocation();
  const isAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();
  const canLeaveNursingHomeScope = useCanLeaveNursingHomeScope();
  const { kind, selectedNursingHome, isScopeRestored } = useScope();

  // Both checks below redirect, so neither may run on a half-known picture. The
  // stored scope is read back asynchronously and reads as `nursing_home` until
  // it lands, and the profile arrives over the network - acting before either
  // does would bounce an employee off a page they are allowed to see, and a
  // reload on such a page would always end up on the dashboard.
  if (!isScopeRestored || canLeaveNursingHomeScope === undefined) {
    return <Outlet />;
  }

  // The admin/manager role is held per nursing home, so it can only gate a
  // route while a single home is in view - and only once it is known which home
  // that is. Above nursing home scope the scope selector has already done the
  // gating: it offers only the legal entities the employee is admin or manager
  // in, and tenant scope only to a global tenant admin (ENG-2503).
  if (
    kind === 'nursing_home' &&
    selectedNursingHome &&
    !isRouteAccessible(location.pathname, isAdminOrManager)
  ) {
    return <Navigate to={nhAppMainPages.DASHBOARD} replace />;
  }

  // The dashboard is supported at every scope, so this cannot bounce.
  if (
    canLeaveNursingHomeScope &&
    !isRouteSupportedAtScope(location.pathname, kind)
  ) {
    return <Navigate to={nhAppMainPages.DASHBOARD} replace />;
  }

  return <Outlet />;
}

export function useCanAccessRoute(path: string): boolean {
  const isNursingHomeScope = useIsNursingHomeScope();
  const isAdminOrManager = useEmployeeIsNursingHomeAdminOrManager();

  // See the comment in RouteAccessGuard: the role only gates routes while a
  // single nursing home is in view.
  return !isNursingHomeScope || isRouteAccessible(path, isAdminOrManager);
}
